<template>
	<div class="order-summary" :class="{ 'hidden': !isVisible }">
		<div class="order-summary-header">RINCIAN PESANAN:</div>
		<div class="order-summary-items">
			<div class="order-summary-item d-flex">
				<div class="summary-item__name summary-product-name">
					<div>
						{{ name }}
						<!-- quantity -->
						<span v-if="quantity !== 0">(x{{ quantity }})</span>
					</div>
					<!-- show selected variant not multiple -->
					<template v-if="productType === 'variable' && !isMultipleVariant && variations.length > 0">
						<div v-for="(item, index) in variations" :key="index">
							<small>{{ item.name }}: {{ item.value }}</small>
						</div>
					</template>
				</div>
				<div class="order-summary-item-normal" v-if="!isMultipleVariant">
					<span class="summary-item__price" v-if="isDiscounted || isWholesale">
						{{ formatMoney(finalProductPrice) }}
					</span>
					<span class="summary-item__price" :class="{'lined-price': isDiscounted || isWholesale }">
						{{ formatMoney(productPrice) }}
					</span>
				</div>
			</div>

			<!-- variations multiple -->
			<div class="order-summary-multiple-variations" v-if="productType === 'variable' && isMultipleVariant && selectedMultipleVariations.length > 0">
				<div
					v-for="(item, index) in selectedMultipleVariations"
					:key="index"
					class="order-summary-item d-flex">
					<div class="summary-item__name">
						{{ item.variant.label_with_attribute_name }} (x{{ item.quantity }})
					</div>
					<div class="summary-item__price">
						<div :class="{'lined-price': isMultipleVariantWholesale(item) }">
							{{ setMultipleVariationPrice(item.variant.price, item.quantity) }}
						</div>
						<div v-if="isMultipleVariantWholesale(item)">
							{{ setMultipleWholesalePrice(item) }}
						</div>
					</div>
				</div>
			</div>

			<!-- bump price -->
			<div class="order-summary-item d-flex">
				<div class="summary-item__name summary-product-bump"></div>
				<div class="summary-item__price summary-product-bump-price"></div>
			</div>

			<!-- shipping -->
			<div class="order-summary-item d-flex summary-shipping-row" v-if="shipping !== null">
				<div class="summary-item__name summary-product-shipping">
					<div class="product-price__shipping-label">Biaya Pengiriman</div>
					<small class="summary-product-total-weight">
						({{ totalWeight }}gr)
					</small>
				</div>
				<div class="summary-item__price summary-product-shipping-price">
					{{ formatMoney(totalShippingCost) }}
				</div>
			</div>

			<!-- COD FEE -->
			<div class="order-summary-item d-flex summary-cod-row" v-if="hasCOD">
				<div class="summary-item__name summary-product-cod">Biaya COD</div>
				<div class="summary-item__price summary-product-cod-cost">
					{{ formatMoney(codCost) }}
				</div>
			</div>

			<!-- coupon -->
			<div class="order-summary-item order-summary-item-discount d-flex" v-if="coupon !== null">
				<div class="summary-item__name">Diskon</div>
				<div class="summary-item__price discount-summary-value">-{{ formatMoney(couponValue) }}</div>
			</div>

			<!-- unique code -->
			<div class="order-summary-item order-summary-item-code d-flex" v-if="hasUniqueCode">
				<div class="product-price-tag">Kode Unik</div>
				<div class="summary-item__price summary-unique-code-value">
					<span v-if="uniqueCodeType === 'decrease'">-</span>{{ formatMoney(uniqueCodePrice) }}
				</div>
			</div>

			<!-- total -->
			<div class="order-summary-item order-summary-item-total d-flex">
				<div class="summary-item__name">Total</div>
				<div class="summary-item__price summary-product-total-price"> {{ formatMoney(totalPrice) }}</div>
			</div>
		</div>
	</div>

</template>

<script>
import {
	// eslint-disable-next-line
	getAxiosErrorMessage,
	// eslint-disable-next-line
	getWholesalePrice,
	// eslint-disable-next-line
	getFinalPrice,
	// eslint-disable-next-line
	getSalePrice,
	// eslint-disable-next-line
	getIsDiscounted,
	// eslint-disable-next-line
	setProductVariationName,
} from '@/lib/helper';

import {
	// eslint-disable-next-line
	paymentChannels,
} from '@/lib/statics';

// const paymentMethods = []
export default {
	name: 'Summary',
	props: {
		username: {
			type: String,
			default: '',
		},
		product: {
			type: Object,
			default: null,
		},
		name: {
			type: String,
			default: '',
		},
		price: {
			type: Number,
			default: 0,
		},
		shipping: {
			type: Object,
			default: null,
		},
		weight: {
			type: Number,
			default: 0,
		},
		uniqueCode: {
			type: Object,
			default: null,
		},
		simpleVariation: {
			type: Object,
			default: null,
		},
		multipleVariation: {
			type: Array,
			default: () => [],
		},
		contact: {
			type: Object,
			default: null,
		},
		productType: {
			type: String,
			default: 'simple',
		},
		selectedPayment: {
			type: Object,
			default: null,
		},
		coupon: {
			type: Object,
			default: null,
		},
		productPrice: {
			type: Number,
			default: 0,
		},
		quantity: {
			type: Number,
			default: 0,
		},
		shippingCost: {
			type: Number,
			default: 0,
		},
	},
	data() {
		return {
			paymentChannels,
			isFetching: false,
			paymentMethods: [],
			uniqueCodePrice: 0,
			codOriginCost: 0,
		};
	},
	computed: {
		selectedPaymentMethod() {
			const method = this.selectedPayment?.method || 'bank_transfer';
			return method;
		},

		totalWeight() {
			let weight = this.quantity * this.weight;
			if (this.isMultipleVariant) {
				const multipleVariation = this.multipleVariation.map((curr) => {
					curr.total_weight = curr.quantity * curr.variant.weight;
					return curr;
				});
				weight = multipleVariation.reduce((totalWeight, item) => (totalWeight + item.total_weight), 0);
			}
			return weight;
		},

		totalShippingCost() {
			const cost = this.shippingCost;
			return cost;
		},

		isMultipleVariant() {
			let varian = false;
			if (this.product !== null && this.product.variations.multiple_variations) {
				varian = this.product.variations.multiple_variations.enabled;
			}
			return varian;
		},

		hasUniqueCode() {
			let uniqueEnabled = false;
			if (this.uniqueCode !== null && this.selectedPaymentMethod === 'bank_transfer') {
				uniqueEnabled = this.uniqueCode.enabled;
			}
			return uniqueEnabled;
		},

		uniqueCodeType() {
			const type = this.uniqueCode?.type || 'increase';
			return type;
		},

		finalProductPrice() {
			const product = this.product;
			let selectedVariation = this.selectedVariation;
			if (this.isMultipleVariant) {
				selectedVariation = this.selectedMultipleVariations;
			}
			const qty = this.quantity;
			const price = getFinalPrice(product, selectedVariation, qty);
			// const finalPrice = price * qty;
			const finalPrice = price;
			return finalPrice;
		},

		salePrice() {
			return getSalePrice(this.product);
		},

		isDiscounted() {
			return getIsDiscounted(this.product);
		},

		isWholesale() {
			let wholesaleStatus = false;
			// product variation
			if (this.productType === 'variable') {
				if (this.selectedVariation) {
					const name = setProductVariationName(this.selectedVariation);
					// console.log('name: ', name);
					const variation = this.product.variations.prices.find(curr => curr.name === name);
					const variationPrice = variation ? variation.price : 0;
					const variationWholesale = variation ? variation.wholesale : [];
					const price = getWholesalePrice(variationWholesale, this.quantity, variationPrice);

					wholesaleStatus = price !== variationPrice;
				}

			// product simple
			} else {
				const wholesales = this.product.wholesale || [];
				const normalPrice = this.isDiscounted ? this.salePrice : this.product.price;
				const price = getWholesalePrice(wholesales, this.quantity, normalPrice);
				wholesaleStatus = price !== normalPrice;
			}
			return wholesaleStatus;
		},

		variations() {
			let variant = [];
			if (this.simpleVariation !== null) {
				// eslint-disable-next-line
				variant = Object.keys(this.simpleVariation).map((key) => {
					return {
						name: key,
						value: this.simpleVariation[key],
					};
				});
			}
			return variant;
		},

		selectedVariation() {
			let varian = null;
			if (this.isMultipleVariant) {
				varian = {};
			} else {
				varian = this.simpleVariation;
			}
			return varian;
		},

		hasCOD() {
			return this.selectedPaymentMethod === 'cod';
		},

		codCost() {
			let cost = 0;
			const shippingCOD = this.product?.shipping?.cod;
			const costPercent = shippingCOD.percentage / 100;
			const minimalCost = shippingCOD.minimal;
			const maximalCost = shippingCOD.maximal;

			if (shippingCOD.feeType === 'product_shipping') {
				cost = costPercent * (this.productPrice + this.shippingCost);

				// eslint-disable-next-line
				this.codOriginCost = cost;
				if (cost <= minimalCost) {
					cost = minimalCost;
				}

				if (cost >= maximalCost) {
					cost = maximalCost;
				}

			// eslint-disable-next-line
			} else if (shippingCOD.feeType === 'product') {
				cost = costPercent * this.productPrice;

				// eslint-disable-next-line
				this.codOriginCost = cost;
				if (cost <= minimalCost) {
					cost = minimalCost;
				}

				if (cost >= maximalCost) {
					cost = maximalCost;
				}
			}

			return cost;
		},

		totalPrice() {
			const couponValue = this.couponValue;
			let total = this.finalProductPrice + this.shippingCost - couponValue;
			const paymentMethod = this.selectedPaymentMethod;

			if (paymentMethod === 'cod') {
				// eslint-disable-next-line
				total = total + this.codCost;
			// append unique code
			} else if (paymentMethod === 'bank_transfer') {
				if (this.hasUniqueCode && this.uniqueCodePrice > 0) {
					if (this.uniqueCodeType === 'decrease') {
						// eslint-disable-next-line
						total = total - this.uniqueCodePrice;
					} else {
						// eslint-disable-next-line
						total = total + this.uniqueCodePrice;
					}
				}
			} else if (paymentMethod === 'instant') {
				const additionalCost = 0;
				// eslint-disable-next-line
				total = total + additionalCost; // this use if payment method instant have additional cost like tax etc
			}

			return total;
		},

		selectedMultipleVariations() {
			const variations = this.multipleVariation.length > 0
				? this.multipleVariation.filter(curr => curr.quantity > 0)
				: [];
			return variations;
		},

		couponValue() {
			let value = 0;
			if (this.coupon !== null && this.coupon.discount) {
				value = this.coupon.discount;
			}
			return value;
		},

		isVisible() {
			// eslint-disable-next-line
			return this.product?.form?.elements?.order_summary?.enabled ?? false;
		},
	},
	watch: {},
	created() {
		this.init();
	},
	methods: {
		init() {},
		setMultipleVariationPrice(price, quantity) {
			const finalPrice = price * quantity;
			return this.formatMoney(finalPrice);
		},
		isMultipleVariantWholesale(value) {
			let wholesaleStatus = false;
			if (value) {
				const qty = value.quantity;
				const name = value.variant.name;
				// console.log('name: ', name);
				const variation = this.product.variations.prices.find(curr => curr.name === name);
				const variationPrice = variation ? variation.price : 0;
				const variationWholesale = variation ? variation.wholesale : [];
				const price = getWholesalePrice(variationWholesale, qty, variationPrice);
				wholesaleStatus = price !== variationPrice;
			}
			return wholesaleStatus;
		},
		setMultipleWholesalePrice(value) {
			let price = 0;
			if (value) {
				const qty = value.quantity;
				const name = value.variant.name;
				// console.log('name: ', name);
				const variation = this.product.variations.prices.find(curr => curr.name === name);
				const variationPrice = variation ? variation.price : 0;
				const variationWholesale = variation ? variation.wholesale : [];
				price = getWholesalePrice(variationWholesale, qty, variationPrice);
				// eslint-disable-next-line
				price = price * qty;
			}
			return this.formatMoney(price);
		},
	},
};
</script>

<style class="scss" scoped>
</style>
